import { allPieceTypes } from "../const";
import {
  IPiece,
  PieceMovement,
  Position,
  IMove,
  PieceType,
  ISquare,
  MoveType,
} from "../types";
import { BaseMovement, BasicSquareInfo } from "./BaseMovement";
import { Move } from "../Move";

export class WolfMovement extends BaseMovement implements PieceMovement {
  capturablePieceTypes: PieceType[] = allPieceTypes;

  // Add a custom getMove method that overrides the base one
  getMove(
    fromPosition: Position,
    toPosition: Position,
    basicSquareInfo: BasicSquareInfo,
    piece: IPiece,
    capturablePieceTypes: PieceType[]
  ): Move | undefined {
    // Wolf can capture Rhino from any position
    if (
      basicSquareInfo.isEnemyOccupied &&
      basicSquareInfo.piece?.type === PieceType.Rhino &&
      capturablePieceTypes.includes(PieceType.Rhino)
    ) {
      return new Move(
        piece,
        toPosition,
        MoveType.capture,
        basicSquareInfo.piece
      );
    }

    // For all other cases, use the base implementation
    return super.getMove(
      fromPosition,
      toPosition,
      basicSquareInfo,
      piece,
      capturablePieceTypes
    );
  }

  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // ZOMBIFICATION: Zombified pieces act as the opposite color
    const effectiveIsLight = piece.zombifiedStatus.isZombified
      ? !piece.isLight
      : piece.isLight;

    const isLone = this.isLoneWolf(piece, boardState, effectiveIsLight);

    const directions = isLone
      ? [
          { col: -1, row: -3 }, // Up-Left
          { col: 1, row: -3 }, // Up-Right
          { col: -1, row: 3 }, // Down-Left
          { col: 1, row: 3 }, // Down-Right
          { col: -3, row: -1 }, // Left-Up
          { col: -3, row: 1 }, // Left-Down
          { col: 3, row: -1 }, // Right-Up
          { col: 3, row: 1 }, // Right-Down
        ]
      : [
          { col: -1, row: -2 }, // Up-Left
          { col: 1, row: -2 }, // Up-Right
          { col: -1, row: 2 }, // Down-Left
          { col: 1, row: 2 }, // Down-Right
          { col: -2, row: -1 }, // Left-Up
          { col: -2, row: 1 }, // Left-Down
          { col: 2, row: -1 }, // Right-Up
          { col: 2, row: 1 }, // Right-Down
        ];

    for (const direction of directions) {
      const toPosition = {
        col: position.col + direction.col,
        row: position.row + direction.row,
      };

      if (!this.onBoard(boardState, toPosition)) continue;

      const toSquare = boardState[toPosition.row][toPosition.col];

      // Wolves can't climb trees at all
      // Skip if it's a friendly tree OR an enemy tree with housed pieces
      if (
        toSquare.Pieces.some((p) => p.type === PieceType.Tree) &&
        (toSquare.Pieces.some(
          (p) => p.type === PieceType.Tree && p.isLight === effectiveIsLight
        ) ||
          toSquare.Pieces.length > 1)
      ) {
        continue;
      }

      const squareInfo = this.getSquareInfo(toSquare, effectiveIsLight);

      if (squareInfo.basicSquareInfo) {
        const move = this.getMove(
          position,
          toPosition,
          squareInfo.basicSquareInfo,
          piece,
          this.capturablePieceTypes
        );

        if (move) {
          possibleMoves.push(move);
        }
      }
    }

    return possibleMoves;
  }

  private isLoneWolf(piece: IPiece, boardState: ISquare[][], effectiveIsLight: boolean): boolean {
    const otherPieces: IPiece[] = boardState.flatMap((row) =>
      row.flatMap((square) =>
        square.Pieces.filter((boardPiece) => boardPiece !== piece)
      )
    );

    const allyNonFaintedPieceOnBoard = otherPieces.some(
      (boardPiece) =>
        boardPiece.isLight === effectiveIsLight && // Use effectiveIsLight for ally detection
        !boardPiece.isFainted
    );

    const allyNotVirus = otherPieces.some(
      (boardPiece) =>
        boardPiece.isLight === effectiveIsLight && // Use effectiveIsLight for ally detection
        boardPiece.type !== PieceType.Virus
    );

    if (allyNonFaintedPieceOnBoard && allyNotVirus) {
      return false;
    }

    return true;
  }
}
